import { PAGE_SIZE } from "./constants";
import { ChangeOrder, NewChangeOrderInput } from "./types";
import { getApiUrl } from '@/lib/api-config';
import { useUser } from "@/contexts/UserContext";
import { useProject } from "@/components/ProjectSelector";
import { useEntityList } from "@/hooks/useEntityList";
import { fetchEntity } from '@/lib/entity-api';
import { uploadEntityAttachments, fetchEntityAttachments, deleteAttachment } from '@/lib/entity-attachments';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';

// Change Order attachments: the shared implementation (lib/entity-attachments.ts)
// -- this file only names the entity type these files belong to.
export function uploadChangeOrderAttachments(parentId: string | undefined, files: File[], companyId: string, userId: string): Promise<GenericAttachment[]> {
  return uploadEntityAttachments('change_order', parentId, files, companyId, userId);
}
export function fetchChangeOrderAttachments(parentId: string): Promise<GenericAttachment[]> {
  return fetchEntityAttachments('change_order', parentId);
}
export function deleteChangeOrderAttachment(attachmentId: string, userId: string | undefined): Promise<boolean> {
  return deleteAttachment(attachmentId, userId);
}

function mapRowToChangeOrder(row: any): ChangeOrder {
  return {
    id: row.id,
    coNumber: row.co_number,
    title: row.title ?? row.description ?? '',
    description: row.description ?? '',
    projectId: row.project_id,
    assigneeId: row.assignee_id ?? null,
    projectName: row.project?.name ?? undefined,
    clientCost: Number(row.client_cost ?? 0),
    builderCost: Number(row.builder_cost ?? 0),
    approvalDeadline: row.approval_deadline ?? undefined,
    status: row.status,
    createdAt: row.created_at,
    author: row.created_by_name || 'Unknown User',
    commentsCount: typeof row.comment_count === 'number' ? row.comment_count : 0,
    attachment_count: typeof row.attachment_count === 'number' ? row.attachment_count : 0,
  };
}

function toPayload(input: Partial<NewChangeOrderInput>) {
  const payload: Record<string, unknown> = {};
  if (input.title !== undefined) payload.title = input.title;
  if (input.description !== undefined) payload.description = input.description;
  if (input.projectId !== undefined && input.projectId.trim()) payload.project_id = input.projectId;
  if (input.assigneeId !== undefined) payload.assignee_id = input.assigneeId || null;
  if (input.clientCost !== undefined) payload.client_cost = input.clientCost;
  if (input.builderCost !== undefined) payload.builder_cost = input.builderCost;
  if (input.approvalDeadline !== undefined) payload.approval_deadline = input.approvalDeadline ?? null;
  if (input.status !== undefined) payload.status = input.status;
  return payload;
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body.error || body.message || fallback;
  } catch {
    return fallback;
  }
}

/** One change order by id, as the list shows it (a link from another document opens it); undefined when it no longer exists or the caller cannot read it. */
export async function fetchChangeOrderById(id: string): Promise<ChangeOrder | undefined> {
  try {
    const row = await fetchEntity<any>('change_order', id, { includeCounts: true, includeCreator: true });
    return row ? mapRowToChangeOrder(row) : undefined;
  } catch (error) {
    console.error('Could not open the change order:', error);
    return undefined;
  }
}

/**
 * The Change Orders list: the generic server-driven list (hooks/useEntityList.ts) with this module's row mapping.
 * Search / sort / project / paging run in the database over every change order the user may see; `exportRows`
 * exports every record of the current query.
 */
export function useChangeOrders() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const list = useEntityList<any, ChangeOrder>({
    entityType: 'change_order',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'append',
    pageSize: PAGE_SIZE,
    includeCounts: true,
    includeCreator: true,
    defaultSort: { columnKey: 'coNumber', direction: 'asc', sortKey: 'co_number' },
    mapRow: mapRowToChangeOrder,
  });
  const { setItems, reload } = list;

  async function addChangeOrder(input: NewChangeOrderInput, author: string): Promise<ChangeOrder> {
    // The server owns company/author/co_number: it derives them from the
    // session, and generates co_number (CO000001, unique per project).
    const response = await fetch(getApiUrl('api/entities/change_order'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toPayload(input)),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to create change order'));

    const created = { ...mapRowToChangeOrder((await response.json()).data), author };
    reload(); // where a new record belongs depends on the server's sort / search
    return created;
  }

  async function updateChangeOrder(id: string, updates: Partial<NewChangeOrderInput>): Promise<ChangeOrder> {
    const response = await fetch(getApiUrl(`api/entities/change_order/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toPayload(updates)),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to update change order'));

    const mapped = mapRowToChangeOrder((await response.json()).data);
    setItems((prev) => prev.map((previous) => (
      previous.id === id ? { ...mapped, projectName: previous.projectId === mapped.projectId ? previous.projectName : undefined, author: previous.author, commentsCount: previous.commentsCount, attachment_count: previous.attachment_count } : previous
    )));
    return mapped;
  }

  return { ...list, addChangeOrder, updateChangeOrder };
}
